<template>
<div class="chapter">
<div class="inner_page">
<p class="fmtitle2"><a id="page_xxi">ON LANGUAGE AND PRONUNCIATION</a></p><a id="page_xxi">
<p class="noindent">Field research for this study took place among native speakers of seven distinct languages: Tuvan, Xakas (Khakas), Altai, Mongolian, Kazakh, Kyrgyz, and Russian. All except Mongolian and Russian are Turkic languages that share many features of syntax, morphology, and phonology. Mongolian does not belong to the Turkic group, but centuries of cultural intermingling between Turkic and Mongol peoples has led to a significant number of shared words, particularly in contemporary Tuvan and Mongolian. Examples include <i>xögjüm</i>, the word that corresponds most closely to “music” in both languages, and <i>xöömei</i> (Tuvan) or <i>höömii</i> (Mongolian), the general term for “throat-singing.”</p>
<p class="indent">With one or two exceptions, all of the musicians and scholars who figure in this work spoke Russian in addition to their native language, and in some cases, Russian <i>was</i> their native language. During the Soviet era, official language policy favored the adoption of Russian by the “small peoples” of Siberia, and many of these groups, among them the Xakas and Altai, experienced severe language loss. Fortunately, Tuvans managed to preserve their language—the 1989 U.S.S.R. census showed that 99.2 percent of Tuvans consider Tuvan their mother tongue, while around 60 percent of Tuvans also know Russian.<sup>10</sup></p>
</a><p class="indentb"><a id="page_xxi">Notwithstanding the dark side of Soviet language policies, the ubiquity of Russian as a language of scholarship in the former U.S.S.R. has made it an indispensable research tool for musical ethnography. In my own case, it has also served as a lingua franca for musical fieldwork: the conversations with musicians quoted in the text were almost all in Russian (in Mongolia, I also benefited from the Mongolian-Russian translations of Mongolian colleagues Erdenechimig and Sansargereltech). Bilingual speakers of Russian and a Turkic language helped me elucidate the meaning of local musical terms, while my own earlier work with Turkic languages (Uzbek and Turkish) provided a grounding that facilitated the translation of song and poetic </a><a id="page_xxii">texts in collaboration with native speakers (see Acknowledgments). In these texts, which appear in the original language as well as in English translation, transcriptions from Turkic languages and Mongolian into the Latin alphabet follow international conventions adopted by present-day scholars. The pronunciation of most letters will be intuitive to English-speakers, but the following pronunciation guide is provided for sounds not found in English, as well as for a few letters with more than one possible English pronunciation:</a></p><a id="page_xxii">
<table cellpadding="0" cellspacing="0" class="table1" width="100%">
<tbody><tr>
<td><p class="tab1"><b>Transcription</b></p></td>
<td><p class="tab1"><b>Sound</b></p></td>
<td><p class="tab1"><b>Example</b></p></td>
</tr>
<tr>
<td><p class="tab1ti">x</p></td>
<td><p class="tab1t">“ch” as in Scottish “loch”</p></td>
<td><p class="tab1t"><i>xöömei</i> (throat-singing)</p></td>
</tr>
<tr>
<td><p class="tab1ti">j</p></td>
<td><p class="tab1t">“j” as in jewel</p></td>
<td><p class="tab1t">jaxsy (good)</p></td>
</tr>
<tr>
<td><p class="tab1i">ö</p></td>
<td><p class="tab1">“u” as in “put”</p></td>
<td><p class="tab1"><i>kök</i> (blue, green)</p></td>
</tr>
<tr>
<td><p class="tab1i">ü</p></td>
<td><p class="tab1">“u” as in “tutor”</p></td>
<td><p class="tab1"><i>xün</i> (sun)</p></td>
</tr>
<tr>
<td><p class="tab1i">q</p></td>
<td><p class="tab1">“c” as in “cot”</p>
<p class="tab1">(a hard “c” formed in</p>
<p class="tab1">the back of the throat)</p></td>
<td><p class="tab1"><i>qil qiyak</i> (two-stringed fiddle)</p></td>
</tr>
<tr>
<td><p class="tab1i">y</p></td>
<td><p class="tab1">“i” as in “lit”</p></td>
<td><p class="tab1"><i>sygyt</i> (style of throat-singing)</p></td>
</tr>
</tbody></table>
<p class="indent">During the Soviet era, spellings of many toponyms and ethnonyms in indigenous languages were Russified, and in the post-Soviet era, these have tended to revert to their pre-Soviet forms. For this study, the most significant of these transformations concerns “Tuva” itself, for Tuva is a Russification of what in Tuvan is pronounced “Tyva” (written in Cyrillic as Tыва). The widespread use of “Tuva,” however, offers a persuasive reason to adopt it in this work.</p>
</a>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// 09_FM01.vue?306d1aa6